import type {
    GroupedCategory,
    IdBooleanObject,
    MapData,
    MapDataCategory,
    MapDataLocationFeature,
    MapDataRegion,
    MapDataRegionGeometry,
    MapDataSubcategory,
} from '@repo/types/maps/map-types';
import { getFontInfoForGlyph, isPointInRegion } from '@repo/types/maps/map-utils';
import type { FilterState } from '../slices/mapData';

export const CUSTOM_PINS_CATEGORY_ID = 'custompins';
export const VIDEO_PINS_SUBCATEGORY_ID = 'videopins';
export const DEFAULT_PIN_ICON = 'map-pin';
export const VIDEO_UPLOAD_PIN_ICON = 'video-camera-alt';
export const locationIdQueryParam = 'location' as const;
export const DEFAULT_PIN_COLOR = '#10BDDB';

interface RegionBounds {
    minLng: number;
    minLat: number;
    maxLng: number;
    maxLat: number;
}

interface RegionPinIndex {
    features: ReadonlyArray<MapDataLocationFeature>;
    regions: ReadonlyArray<MapDataRegion>;
    regionsById: Map<string, MapDataRegion>;
    pinIdsByRegionId: Map<string, Set<string>>;
}

const regionPinIndexCache = new WeakMap<MapData, RegionPinIndex>();

const getRegionBounds = (geometry: MapDataRegionGeometry): RegionBounds => {
    const bounds = {
        minLng: Number.POSITIVE_INFINITY,
        minLat: Number.POSITIVE_INFINITY,
        maxLng: Number.NEGATIVE_INFINITY,
        maxLat: Number.NEGATIVE_INFINITY,
    };

    const addPoint = ([lng, lat]: [number, number]) => {
        bounds.minLng = Math.min(bounds.minLng, lng);
        bounds.minLat = Math.min(bounds.minLat, lat);
        bounds.maxLng = Math.max(bounds.maxLng, lng);
        bounds.maxLat = Math.max(bounds.maxLat, lat);
    };

    const addPolygon = (polygon: ReadonlyArray<ReadonlyArray<[number, number]>>) => {
        for (const ring of polygon) {
            for (const point of ring) {
                addPoint(point);
            }
        }
    };

    if (geometry.type === 'Polygon') {
        addPolygon(geometry.coordinates as ReadonlyArray<ReadonlyArray<[number, number]>>);
    } else {
        for (const polygon of geometry.coordinates as ReadonlyArray<ReadonlyArray<ReadonlyArray<[number, number]>>>) {
            addPolygon(polygon);
        }
    }

    return bounds;
};

const isPointInBounds = ([lng, lat]: [number, number], bounds: RegionBounds) =>
    lng >= bounds.minLng && lng <= bounds.maxLng && lat >= bounds.minLat && lat <= bounds.maxLat;

const getRegionPinIndex = (mapData: MapData, regions: ReadonlyArray<MapDataRegion>) => {
    const cached = regionPinIndexCache.get(mapData);
    if (cached?.features === mapData.geojson.features && cached.regions === regions) {
        return cached;
    }

    const index: RegionPinIndex = {
        features: mapData.geojson.features,
        regions,
        regionsById: new Map(regions.map(region => [region.properties.externalId, region])),
        pinIdsByRegionId: new Map(),
    };
    regionPinIndexCache.set(mapData, index);
    return index;
};

const getPinIdsInRegion = (mapData: MapData, index: RegionPinIndex, regionId: string) => {
    const cached = index.pinIdsByRegionId.get(regionId);
    if (cached) return cached;

    const pinIds = new Set<string>();
    const region = index.regionsById.get(regionId);
    if (!region) {
        index.pinIdsByRegionId.set(regionId, pinIds);
        return pinIds;
    }

    const bounds = getRegionBounds(region.geometry);
    for (const feature of mapData.geojson.features) {
        if (feature.geometry.type !== 'Point') continue;

        const point = feature.geometry.coordinates as [number, number];
        if (!isPointInBounds(point, bounds)) continue;
        if (isPointInRegion(point, region.geometry)) {
            pinIds.add(feature.properties.externalId);
        }
    }

    index.pinIdsByRegionId.set(regionId, pinIds);
    return pinIds;
};

const getPinIdsInRegions = (mapData: MapData, index: RegionPinIndex, regionIds: readonly string[]) => {
    const pinIds = new Set<string>();
    for (const regionId of regionIds) {
        for (const pinId of getPinIdsInRegion(mapData, index, regionId)) {
            pinIds.add(pinId);
        }
    }
    return pinIds;
};

const getEnhancedMetadata = (mapData: MapData) => {
    const categories = [...mapData.metadata.categories];
    const subcategories = [...mapData.metadata.subcategories];

    const customPinsCategory = categories.find(cat => cat.externalId === CUSTOM_PINS_CATEGORY_ID);
    const customPinsSubcategory = subcategories.find(sub => sub.externalId === CUSTOM_PINS_CATEGORY_ID);
    const videoPinsSubcategory = subcategories.find(sub => sub.externalId === VIDEO_PINS_SUBCATEGORY_ID);

    // Add custom pins category if it doesn't exist
    if (!customPinsCategory) {
        categories.push({
            externalId: CUSTOM_PINS_CATEGORY_ID,
            title: 'My Pins',
            color: DEFAULT_PIN_COLOR,
            visibleByDefault: true,
            excludeFromChecklists: false,
        });
    }

    // Add custom pins subcategory if it doesn't exist
    if (!customPinsSubcategory) {
        subcategories.push({
            externalId: CUSTOM_PINS_CATEGORY_ID,
            title: 'My Pins',
            icon: DEFAULT_PIN_ICON,
            categoryExternalId: CUSTOM_PINS_CATEGORY_ID,
            excludeFromChecklists: false,
        });
    }

    // Add video pins subcategory if it doesn't exist
    if (!videoPinsSubcategory) {
        subcategories.push({
            externalId: VIDEO_PINS_SUBCATEGORY_ID,
            title: 'Video Pins',
            icon: VIDEO_UPLOAD_PIN_ICON,
            categoryExternalId: CUSTOM_PINS_CATEGORY_ID,
            excludeFromChecklists: false,
        });
    }

    return { categories, subcategories };
};

export const getLocationMappings = (mapData: MapData) => {
    const categories: { [locationId: string]: MapDataCategory } = {};
    const subcategories: { [locationId: string]: MapDataSubcategory } = {};

    const { categories: metadataCategories, subcategories: metadataSubcategories } = getEnhancedMetadata(mapData);

    mapData.geojson.features.forEach(loc => {
        const sub = metadataSubcategories.find(s => s.externalId === loc.properties.subcategoryExternalId);
        if (sub) {
            subcategories[loc.properties.externalId] = sub;
            const cat = metadataCategories.find(c => c.externalId === sub.categoryExternalId);
            if (cat) {
                categories[loc.properties.externalId] = cat;
            }
        }
    });

    return { categories, subcategories };
};

export const groupCategories = (mapData: MapData, foundPins: IdBooleanObject): GroupedCategory[] => {
    const subcategoryCounts = new Map<string, { locationCount: number; foundLocationCount: number }>();

    for (const feature of mapData.geojson.features) {
        const subcategoryId = feature.properties.subcategoryExternalId;
        if (!subcategoryCounts.has(subcategoryId)) {
            subcategoryCounts.set(subcategoryId, { locationCount: 0, foundLocationCount: 0 });
        }

        const counts = subcategoryCounts.get(subcategoryId) || { locationCount: 0, foundLocationCount: 0 };
        counts.locationCount++;

        if (foundPins[feature.properties.externalId]) {
            counts.foundLocationCount++;
        }
    }

    const { categories, subcategories } = getEnhancedMetadata(mapData);

    return categories
        .map(cat => ({
            externalId: cat.externalId,
            title: cat.title,
            color: cat.color,
            original: cat,
            subcategories: subcategories
                .filter(sub => sub.categoryExternalId === cat.externalId)
                .map(sub => {
                    const counts = subcategoryCounts.get(sub.externalId) || {
                        locationCount: 0,
                        foundLocationCount: 0,
                    };
                    return {
                        externalId: sub.externalId,
                        title: sub.title,
                        icon: sub.icon,
                        fontName: getFontInfoForGlyph(sub.icon).fontName,
                        locationCount: counts.locationCount,
                        foundLocationCount: counts.foundLocationCount,
                        original: sub,
                    };
                }),
        }))
        .sort((a, b) => {
            if (a.externalId === CUSTOM_PINS_CATEGORY_ID) return -1;
            if (b.externalId === CUSTOM_PINS_CATEGORY_ID) return 1;

            return 0;
        });
};

const shouldShowFeature = (
    feature: MapDataLocationFeature,
    filters: FilterState,
    foundPins: IdBooleanObject,
    locationCategories: { [locationId: string]: MapDataCategory },
    selectedMarkerId: string | null,
    activeCategoryId: string | null,
    hiddenSubcategoriesSet: Set<string>,
    searchTermLower: string,
    subcategoryLookup: { [externalId: string]: MapDataSubcategory },
    regionPinIds: Set<string> | null,
    visibleRegionPinIds: Set<string> | null,
    includeHiddenSubcategoriesWhenSearching: boolean,
): boolean => {
    const externalId = feature.properties.externalId;

    // Always show the selected marker regardless of filters
    if (selectedMarkerId === externalId) return true;

    // Active category acts as a lens — only show pins from that category
    if (activeCategoryId) {
        const category = locationCategories[externalId];
        return category?.externalId === activeCategoryId;
    }

    // Check if the subcategory is hidden
    if (
        hiddenSubcategoriesSet.has(feature.properties.subcategoryExternalId) &&
        !(includeHiddenSubcategoriesWhenSearching && searchTermLower)
    ) {
        return false;
    }

    if (visibleRegionPinIds) {
        if (filters.regionFilterMode === 'include') {
            if (!visibleRegionPinIds.has(externalId)) return false;
        } else if (regionPinIds?.has(externalId) && !visibleRegionPinIds.has(externalId)) {
            return false;
        }
    }

    const category = locationCategories[externalId];

    if (filters.teleportOnly && !feature.properties.gameCoords) return false;

    if (filters.unfoundOnly && foundPins[externalId]) return false;

    if (searchTermLower) {
        const titleMatch = feature.properties.title?.toLowerCase().includes(searchTermLower);
        const descriptionMatch = feature.properties.description?.toLowerCase().includes(searchTermLower);
        const externalIdMatch = feature.properties.externalId?.toLowerCase().includes(searchTermLower);
        const categoryMatch = category?.title?.toLowerCase().includes(searchTermLower) ?? false;
        const subcategory = subcategoryLookup[feature.properties.subcategoryExternalId];
        const subcategoryMatch = subcategory?.title?.toLowerCase().includes(searchTermLower) ?? false;

        return titleMatch || descriptionMatch || externalIdMatch || categoryMatch || subcategoryMatch;
    }

    return true;
};

export const getFilteredFeatures = (
    mapData: MapData | null,
    filters: FilterState,
    foundPins: IdBooleanObject,
    locationCategories: { [locationId: string]: MapDataCategory },
    selectedMarkerId: string | null,
    activeCategoryId: string | null = null,
    isInCreatorMode: boolean = false,
    options: { includeHiddenSubcategoriesWhenSearching?: boolean } = {},
): MapDataLocationFeature[] => {
    if (!mapData) return [];

    // Pre-compute expensive operations once
    const hiddenSubcategoriesSet = new Set(filters.hiddenSubcategories);
    const searchTermLower = filters.searchTerm.toLowerCase().trim();

    const hiddenRegionSet = new Set(filters.hiddenRegions);
    let regionPinIds: Set<string> | null = null;
    let visibleRegionPinIds: Set<string> | null = null;
    const regions = mapData.regions?.features;
    if ((hiddenRegionSet.size > 0 || filters.regionFilterMode === 'include') && regions?.length) {
        const hiddenRegionIds: string[] = [];
        const visibleRegionIds: string[] = [];
        for (const region of regions) {
            if (hiddenRegionSet.has(region.properties.externalId)) {
                hiddenRegionIds.push(region.properties.externalId);
            } else {
                visibleRegionIds.push(region.properties.externalId);
            }
        }

        const regionPinIndex = getRegionPinIndex(mapData, regions);
        if (filters.regionFilterMode === 'include') {
            visibleRegionPinIds = getPinIdsInRegions(mapData, regionPinIndex, visibleRegionIds);
        } else if (hiddenRegionIds.length > 0) {
            const regionIds = regions.map(region => region.properties.externalId);
            // Region filters only apply to pins that belong to at least one region.
            regionPinIds = getPinIdsInRegions(mapData, regionPinIndex, regionIds);
            visibleRegionPinIds = getPinIdsInRegions(mapData, regionPinIndex, visibleRegionIds);
        }
    }

    // Create subcategory lookup for search functionality
    const subcategoryLookup: { [externalId: string]: MapDataSubcategory } = {};
    if (mapData?.metadata.subcategories) {
        for (const sub of mapData.metadata.subcategories) {
            subcategoryLookup[sub.externalId] = sub;
        }
    }

    return mapData.geojson.features.filter(feature => {
        // Hide custom pins when in creator mode
        if (isInCreatorMode && feature.properties.isCustomPin) {
            return false;
        }

        return shouldShowFeature(
            feature,
            filters,
            foundPins,
            locationCategories,
            selectedMarkerId,
            activeCategoryId,
            hiddenSubcategoriesSet,
            searchTermLower,
            subcategoryLookup,
            regionPinIds,
            visibleRegionPinIds,
            options.includeHiddenSubcategoriesWhenSearching ?? false,
        );
    });
};
